/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useLayoutEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { findOverflowScrollParent } from '@atlaskit/editor-common/Popup/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { RovoThinkingReady } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingReady';

import { createDismissInlineEditReadyCommand } from '../../../pm-plugins/decoration/actions';
import { resolveInlineEditTarget } from './inline-edit-target';

const messages = defineMessages({
	ready: {
		id: 'fabric.editor.ai.inlineEditReadyBar.ready',
		defaultMessage: 'Your edits are ready',
		description: 'Text shown on the floating bar when an inline edit completes',
	},
});

const styles = cssMap({
	// Floats the ready bar at the bottom-centre of the viewport, matching the
	// other floating thinking/ready bars.
	floatingReadyBar: {
		position: 'fixed',
		bottom: token('space.200'),
		left: '50%',
		transform: 'translateX(-50%)',
		width: 'fit-content',
		// 400px matches the shared Rovo floating ready bar design minimum; no ADS sizing token maps to it.
		minWidth: '400px',
		zIndex: 600,
	},
});

interface InlineEditReadyBarProps {
	editorView: EditorView;
	targetId: string | undefined;
}

/**
 * Floating "ready" bar shown after an inline edit completes. Lives in the
 * always-mounted content component (driven by plugin state) so it survives the
 * prompt/session teardown. View scrolls to the edited node; Close dismisses.
 */
export const InlineEditReadyBar = ({
	editorView,
	targetId,
}: InlineEditReadyBarProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isVisibilityFixEnabled = isExperimentEnabled('cc_maui_polish_changes_batch_5');
	// Keep the bar hidden until the gated viewport check completes, preventing an in-view edit from
	// flashing the ready state for one frame.
	const [shouldShowReadyBar, setShouldShowReadyBar] = useState(!isVisibilityFixEnabled);

	const dismiss = useCallback(() => {
		createDismissInlineEditReadyCommand()(editorView.state, editorView.dispatch);
	}, [editorView]);

	const resolveTarget = useCallback(
		() => resolveInlineEditTarget({ editorView, targetId }),
		[editorView, targetId],
	);

	useLayoutEffect(() => {
		if (!isVisibilityFixEnabled) {
			setShouldShowReadyBar(true);
			return;
		}

		const target = resolveTarget();
		const editorDocument = editorView.dom.ownerDocument;
		const IntersectionObserver = editorDocument.defaultView?.IntersectionObserver;
		if (!target || !IntersectionObserver) {
			setShouldShowReadyBar(true);
			return;
		}
		const root = findOverflowScrollParent(editorView.dom) || editorDocument.body;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					dismiss();
				} else {
					setShouldShowReadyBar(true);
				}
			},
			{
				root: root === editorDocument.body ? null : root,
			},
		);
		observer.observe(target);

		return () => observer.disconnect();
	}, [dismiss, editorView, isVisibilityFixEnabled, resolveTarget]);

	const handleView = useCallback(() => {
		// preserves the edited node id through completion. Fall back to the historical
		// selection-based target if it cannot be resolved or the experiment is disabled.
		try {
			let node = isVisibilityFixEnabled ? resolveTarget() : null;
			if (!node) {
				const { from } = editorView.state.selection;
				const domAtPos = editorView.domAtPos(from);
				node = domAtPos.node instanceof HTMLElement ? domAtPos.node : domAtPos.node.parentElement;
			}
			node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		} catch {
			// domAtPos can throw if the position is out of range; ignore silently.
		}
		dismiss();
	}, [dismiss, editorView, isVisibilityFixEnabled, resolveTarget]);

	if (!shouldShowReadyBar) {
		return null;
	}

	return (
		<Box xcss={styles.floatingReadyBar}>
			<RovoThinkingBarWrapper>
				<RovoThinkingReady
					onView={handleView}
					onClose={dismiss}
					readyText={formatMessage(messages.ready)}
				/>
			</RovoThinkingBarWrapper>
		</Box>
	);
};
